/**
 * Centralized API Paths Configuration
 *
 * This file defines all semantic API paths following enterprise-grade REST conventions.
 * All paths use service prefixes: /backend, /user, /ai, /integration
 *
 * Usage:
 *   import { API } from './config/api.paths';
 *   API.backend.projects.search()
 *   API.user.auth.loginOtpSend()
 */

import { API_BASE_PREFIXED, API_BASE } from "./api.config";

/**
 * Backend Service API Paths
 */
export const BackendAPI = {
  // Projects
  projects: {
    search: () => `${API_BASE_PREFIXED.backend}/projects/search`,
    get: () => `${API_BASE_PREFIXED.backend}/projects/get`,
    create: () => `${API_BASE_PREFIXED.backend}/projects/create`,
    update: () => `${API_BASE_PREFIXED.backend}/projects/update`,
    delete: () => `${API_BASE_PREFIXED.backend}/projects/delete`,
    deactivate: () => `${API_BASE_PREFIXED.backend}/projects/deactivate`,
    activate: () => `${API_BASE_PREFIXED.backend}/projects/activate`,
    types: () => `${API_BASE_PREFIXED.backend}/projects/types`,
    users: {
      list: () => `${API_BASE_PREFIXED.backend}/projects/users/list`,
      assign: () => `${API_BASE_PREFIXED.backend}/projects/users/assign`,
    },
  },

  // Test Cases
  testcases: {
    search: () => `${API_BASE_PREFIXED.backend}/testcases/search`,
    get: () => `${API_BASE_PREFIXED.backend}/testcases/get`,
    create: () => `${API_BASE_PREFIXED.backend}/testcases/create`,
    update: () => `${API_BASE_PREFIXED.backend}/testcases/update`,
    delete: () => `${API_BASE_PREFIXED.backend}/testcases/delete`,
    assign: () => `${API_BASE_PREFIXED.backend}/testcase/assign`,
    bulk: {
      update: () => `${API_BASE_PREFIXED.backend}/testcases/bulk/update`,
      delete: () => `${API_BASE_PREFIXED.backend}/testcases/bulk/delete`,
    },
    steps: {
      list: () => `${API_BASE_PREFIXED.backend}/testcases/steps/list`,
      create: () => `${API_BASE_PREFIXED.backend}/testcases/steps/create`,
      bulk: {
        create: () =>
          `${API_BASE_PREFIXED.backend}/testcases/steps/bulk/create`,
      },
    },
  },

  // Defects
  defects: {
    list: () => `${API_BASE_PREFIXED.backend}/defects`,
    get: (id) => `${API_BASE_PREFIXED.backend}/defects/${id}`,
    create: () => `${API_BASE_PREFIXED.backend}/defects`,
    update: (id) => `${API_BASE_PREFIXED.backend}/defects/${id}`,
    search: () => `${API_BASE_PREFIXED.backend}/defects/search`,
    statusCounts: () => `${API_BASE_PREFIXED.backend}/defects/status-counts`,
    duplicates: () => `${API_BASE_PREFIXED.backend}/defects/duplicates`,
    sync: () => `${API_BASE_PREFIXED.backend}/defects/sync`,
    link: (id) => `${API_BASE_PREFIXED.backend}/defects/${id}/link`,
  },

  // Test Plans (path-only — axios baseURL is …/backend; avoids double-/backend and wrong merges)
  testplans: {
    list: () => "testplan/list",
    get: () => "testplan/get",
    create: () => "testplan/create",
    update: () => "testplan/update",
    delete: () => "testplan/delete",
    archive: () => "testplan/archive",
    restore: () => "testplan/restore",
    complete: () => "testplan/complete",
    linkRun: () => "testplan/link-run",
    createRun: () => "testplan/create-run",
    progress: () => "testplan/progress",
    bulkArchive: () => "testplan/bulk-archive",
    bulkRestore: () => "testplan/bulk-restore",
    bulkComplete: () => "testplan/bulk-complete",
    bulkDelete: () => "testplan/bulk-delete",
  },

  // Test Runs
  testruns: {
    list: () => `${API_BASE_PREFIXED.backend}/testruns/list`,
    get: () => `${API_BASE_PREFIXED.backend}/testruns/get`,
    create: () => `${API_BASE_PREFIXED.backend}/testruns/create`,
    update: () => `${API_BASE_PREFIXED.backend}/testruns/update`,
    delete: () => `${API_BASE_PREFIXED.backend}/testruns/delete`,
    executions: {
      update: () => `${API_BASE_PREFIXED.backend}/testruns/executions/update`,
      bulk: {
        update: () =>
          `${API_BASE_PREFIXED.backend}/testruns/executions/bulk/update`,
      },
    },
    testcases: {
      list: () => `${API_BASE_PREFIXED.backend}/testruns/testcases/list`,
    },
  },

  // Health
  health: {
    check: () => `${API_BASE_PREFIXED.backend}/health`,
    db: () => `${API_BASE_PREFIXED.backend}/health/db`,
  },

  // Soft-deleted entities (trash)
  deleted: {
    list: () => "deleted/list",
  },
};

/**
 * User Service API Paths
 */
export const UserAPI = {
  // Authentication
  auth: {
    register: () => `${API_BASE_PREFIXED.user}/auth/register`,
    signup: () => `${API_BASE_PREFIXED.user}/api/auth/signup`,
    login: {
      otp: {
        send: () => `${API_BASE_PREFIXED.user}/auth/login/otp/send`,
        verify: () => `${API_BASE_PREFIXED.user}/auth/login/otp/verify`,
      },
      login: () => `${API_BASE_PREFIXED.user}/auth/login`,
      verifyMfa: () => `${API_BASE_PREFIXED.user}/auth/login/verify-mfa`,
    },
    mfaSetup: {
      init: () => `${API_BASE_PREFIXED.user}/auth/mfa/setup/init`,
      confirm: () => `${API_BASE_PREFIXED.user}/auth/mfa/setup/confirm`,
    },
    logout: () => `${API_BASE_PREFIXED.user}/auth/logout`,
    token: {
      validate: () => `${API_BASE_PREFIXED.user}/auth/token/validate`,
    },
    device: {
      request: () => `${API_BASE_PREFIXED.user}/auth/device/request`,
      details: (requestId) => `${API_BASE_PREFIXED.user}/auth/device/request/${requestId}`,
      status: (requestId) => `${API_BASE_PREFIXED.user}/auth/device/request/${requestId}/status`,
      approve: (requestId) => `${API_BASE_PREFIXED.user}/auth/device/request/${requestId}/approve`,
      reject: (requestId) => `${API_BASE_PREFIXED.user}/auth/device/request/${requestId}/reject`,
    },
  },

  // Profile
  profile: {
    me: () => `${API_BASE_PREFIXED.user}/profile/me`,
  },

  // MFA (authenticated)
  mfa: {
    status: () => `${API_BASE_PREFIXED.user}/mfa/status`,
    setup: () => `${API_BASE_PREFIXED.user}/mfa/setup`,
    verifySetup: () => `${API_BASE_PREFIXED.user}/mfa/verify-setup`,
    disable: () => `${API_BASE_PREFIXED.user}/mfa/disable`,
  },

  // Health
  health: {
    check: () => `${API_BASE_PREFIXED.user}/health`,
    db: () => `${API_BASE_PREFIXED.user}/health/db`,
  },
};

/**
 * AI Service API Paths
 */
export const AIAPI = {
  testcases: {
    generate: () => `${API_BASE.ai}/api/ai/generate-testcases`,
  },
  modes: () => `${API_BASE.ai}/api/ai/modes`,
};

/**
 * Integration Service API Paths
 */
export const IntegrationAPI = {
  config: {
    save: () => `${API_BASE_PREFIXED.integration}/api/auth/config/save`,
    get: () => `${API_BASE_PREFIXED.integration}/api/auth/config/get`,
  },
  slack: {
    notifications: {
      send: () =>
        `${API_BASE_PREFIXED.integration}/api/slack/notifications/send`,
    },
  },
  jira: {
    issues: {
      search: () => `${API_BASE_PREFIXED.integration}/api/jira/search`,
    },
  },
};

/**
 * Unified API object for easy access
 */
export const API = {
  backend: BackendAPI,
  user: UserAPI,
  ai: AIAPI,
  integration: IntegrationAPI,
};

export default API;
